import { isiOS } from './app';

declare global {
    interface WindowEventMap {
        beforeinstallprompt: BeforeInstallPromptEvent;
    }

    /**
     * The BeforeInstallPromptEvent is fired at the Window.onbeforeinstallprompt handler
     * before a user is prompted to "install" a web site to a home screen on mobile.
     *
     * @deprecated Only supported on Chrome and Android Webview.
     */
    interface BeforeInstallPromptEvent extends Event {
        /**
         * Returns an array of DOMString items containing the platforms on which the event was dispatched.
         * This is provided for user agents that want to present a choice of versions to the user such as,
         * for example, "web" or "play" which would allow the user to chose between a web version or
         * an Android version.
         */
        readonly platforms: Array<string>;

        /**
         * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
         */
        readonly userChoice: Promise<{
            outcome: 'accepted' | 'dismissed';
            platform: string;
        }>;

        /**
         * Allows a developer to show the install prompt at a time of their own choosing.
         * This method returns a Promise.
         */
        prompt(): Promise<void>;
    }
}

type Callback = (available: boolean) => void;

let installationPromptEvent: BeforeInstallPromptEvent | null = null;
const promptChangeCallbacks: Callback[] = [];

/** Whether the browser's install dialog can be opened (Chromium browsers only). */
export const canPrompt = (): boolean => installationPromptEvent != null;

/**
 * Whether the app can be installed at all: via the browser's install dialog, or on iOS/iPadOS
 * manually ("Teilen" -> "Zum Home-Bildschirm"), where no install dialog/event exists.
 */
export const canInstall = (): boolean => canPrompt() || isiOS();

const dispatchPromptChangeEvent = () => {
    for (const callback of promptChangeCallbacks) {
        callback(canPrompt());
    }
};

export const addPromptChangeListener = (callback: Callback) => {
    promptChangeCallbacks.push(callback);
};

export const removePromptChangeListener = (callback: Callback) => {
    const index = promptChangeCallbacks.indexOf(callback);
    if (index >= 0) {
        promptChangeCallbacks.splice(index, 1);
    }
};

/**
 * Starts listening for the install events. Call this once, as early as possible: Chromium fires
 * beforeinstallprompt shortly after the page became installable, and may fire it again later
 * (e.g. after the dialog was dismissed).
 */
export const register = () => {
    window.addEventListener('beforeinstallprompt', (event) => {
        // Suppress the browser's own mini-infobar, we offer the dialog via our own buttons.
        event.preventDefault();
        installationPromptEvent = event;
        dispatchPromptChangeEvent();
    });

    // Installed (via our button or the browser menu): the stored event can't be used anymore.
    window.addEventListener('appinstalled', () => {
        installationPromptEvent = null;
        dispatchPromptChangeEvent();
    });
};

/**
 * Opens the browser's install dialog. Resolves with true if the user accepted.
 */
export const prompt = async (): Promise<boolean> => {
    const event = installationPromptEvent;
    if (event == null) {
        return false;
    }

    // The event can only be used once.
    installationPromptEvent = null;
    dispatchPromptChangeEvent();

    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
};
